/**
 * localGit — main-thread driver for the OPFS-only git worker.
 *
 * A LocalGitSession owns ONE warm worker for a single draft id, so the
 * ~1MB wasm-git module + OPFS backend load once and stay resident across
 * the many commits an editing session produces (auto-commit on save). Ops
 * are serialized (the lg2 instance is single-threaded) behind a lock.
 *
 * Two ops:
 *   - commit(): serialize the live doc and commit it locally (builds history)
 *   - exportZip(): commit, read the whole repo (incl .git), zip it, download
 *
 * Neither touches the network — going online is the cloud publish path.
 */
import { zipSync } from "fflate";
import { withTimeout } from "./withTimeout.js";
import { useGridStore } from "@fredericrous/grid";
import { getAssetFile } from "@website-builder/editor";

import { buildSiteFiles, type BuildSiteFilesInput } from "./buildSiteFiles.js";
import { collectImageAssets, assetKey } from "./siteAssets.js";

interface RepoEntry {
  path: string;
  bytes: Uint8Array;
}

interface WorkerResult {
  op: "commit" | "export";
  ok: boolean;
  error?: string;
  entries?: RepoEntry[];
  stdout: string[];
  stderr: string[];
}

export interface LocalCommitOutcome {
  readonly ok: boolean;
  readonly error?: string;
}

/** Startup is just module + wasm load — much tighter than an op budget. */
const WORKER_START_TIMEOUT_MS = 30_000;

export class LocalGitSession {
  private worker: Worker | null = null;
  private ready: Promise<void> | null = null;
  /** Serializes ops — the lg2 instance can't run two at once. */
  private lock: Promise<unknown> = Promise.resolve();

  constructor(private readonly draftId: string) {}

  private ensureWorker(): Worker {
    if (this.worker) return this.worker;
    const worker = new Worker(new URL("./localGitWorker.ts", import.meta.url), {
      type: "module",
    });
    // The handshake needs its OWN failure path: `run()` only attaches an
    // error listener AFTER `await this.ready`, so a worker whose module or
    // wasm-git bootstrap fails to load never resolves and never rejects —
    // every op queued behind it hangs for the life of the page.
    this.ready = withTimeout(
      new Promise<void>((resolve, reject) => {
        const done = () => {
          worker.removeEventListener("message", onReady);
          worker.removeEventListener("error", onError);
        };
        const onReady = (e: MessageEvent) => {
          if ((e.data as { ready?: boolean })?.ready) {
            done();
            resolve();
          }
        };
        const onError = (e: ErrorEvent) => {
          done();
          reject(new Error(e.message || "local git worker failed to start"));
        };
        worker.addEventListener("message", onReady);
        worker.addEventListener("error", onError);
      }),
      "local git worker startup",
      WORKER_START_TIMEOUT_MS,
    );
    // A failed startup must not resurface as an unhandled rejection on every
    // later `await this.ready`; `run()` surfaces it to its own caller.
    this.ready.catch(() => undefined);
    this.worker = worker;
    return worker;
  }

  private run(req: unknown): Promise<WorkerResult> {
    // Chain on `lock` so ops never overlap; capture+return this op's result.
    const result = this.lock.then(async () => {
      const worker = this.ensureWorker();
      await this.ready;
      return await new Promise<WorkerResult>((resolve, reject) => {
        const onMessage = (e: MessageEvent) => {
          worker.removeEventListener("message", onMessage);
          worker.removeEventListener("error", onError);
          resolve(e.data as WorkerResult);
        };
        const onError = (e: ErrorEvent) => {
          worker.removeEventListener("message", onMessage);
          worker.removeEventListener("error", onError);
          reject(new Error(e.message || "local git worker error"));
        };
        worker.addEventListener("message", onMessage);
        worker.addEventListener("error", onError);
        worker.postMessage(req);
      });
    });
    // Bound the whole chained op, not just the postMessage — a worker that
    // stops answering mid-commit would otherwise wedge the lock permanently.
    const bounded = withTimeout(result, "local git op");
    // Keep the lock resolved even if this op rejects/times out, so the chain
    // survives. Gate on `bounded`: chaining the unbounded `result` would let a
    // hung op block every queued op behind it despite the timeout.
    this.lock = bounded.catch(() => undefined);
    return bounded;
  }

  /** Commit the current editor state to the local repo. Best-effort. */
  async commit(
    input: BuildSiteFilesInput,
    message: string,
  ): Promise<LocalCommitOutcome> {
    const { files } = buildSiteFiles(input);
    const r = await this.run({
      op: "commit",
      siteId: this.draftId,
      files,
      message,
    });
    return { ok: r.ok, ...(r.error ? { error: r.error } : {}) };
  }

  /**
   * Commit the current state, then build a .zip of the repo (working tree +
   * .git, i.e. full history) and trigger a browser download.
   */
  async exportZip(
    input: BuildSiteFilesInput,
    filename: string,
  ): Promise<LocalCommitOutcome> {
    const { files } = buildSiteFiles(input);
    const r = await this.run({
      op: "export",
      siteId: this.draftId,
      files,
      message: `Export ${new Date().toISOString()}`,
    });
    if (!r.ok || !r.entries) {
      const diag = [r.error, ...(r.stderr ?? [])].filter(Boolean).join(" · ");
      return {
        ok: false,
        error: diag || "export failed",
      };
    }
    const zipInput: Record<string, Uint8Array> = {};
    // 1. the git repo (source + full .git history)
    for (const e of r.entries) zipInput[e.path] = e.bytes;
    // 2. the content-addressed image bytes, laid out under the served object
    //    key `_assets/<sha>` (no extension) so the unzipped dir is both
    //    `git clone`-able AND `aws s3 sync`-able for media. Images live in
    //    OPFS under the draft id (this.draftId); git only carries references.
    const assets = collectImageAssets(useGridStore.getState().grids);
    for (const a of assets) {
      const file = await getAssetFile(this.draftId, a.sha256, a.ext);
      if (!file) continue; // referenced but not in local storage — skip
      zipInput[assetKey(a.sha256)] = new Uint8Array(await file.arrayBuffer());
    }
    const zipped = zipSync(zipInput, { level: 6 });
    triggerDownload(zipped, filename);
    return { ok: true };
  }

  dispose(): void {
    this.worker?.terminate();
    this.worker = null;
    this.ready = null;
  }
}

function triggerDownload(bytes: Uint8Array, filename: string): void {
  const blob = new Blob([bytes as unknown as BlobPart], {
    type: "application/zip",
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  // Revoke after the click has a chance to start the download.
  setTimeout(() => URL.revokeObjectURL(url), 10_000);
}
